import React, {
  memo,
  useRef,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useMemo,
} from "react";
import { Container, Sprite } from "@pixi/react";
import * as PIXI from "pixi.js";
import { Settings } from "../../../settings";
import { OVERLAY_BACKGROUND_COLOR } from "../Overlay/OverlayController";

import anime from "animejs";

// ── How dark the non-winning symbols go ──
//
// A black sprite over the reels, so this is the share of the board that is
// COVERED: at 0.8 the symbols underneath were left at a fifth of themselves and
// read as almost solid black. Client feedback from demo 1 called the same thing
// out on the popup overlay, which came down to 0.7 for the same reason.
//
// 0.8 was never chosen by eye — it was derived to imitate an older tint-based
// dim (0x555555), and imitating a tint with an opacity does not carry over.
// 0.8 -> 0.7 -> 0.6.
//
// ONE file, BOTH rooms: GameViewSimple and BonusGameView each render this same
// component, so this number is the whole game's win dim.
//
// 0.6 also matches the per-reel TENSION dims (Games/Main/DimReelOverlay and the
// FreeSpinBasic one) — same number now, but still three separate constants on
// purpose. They are different effects at different moments: this one covers the
// whole board behind a win presentation, those cover one reel during
// anticipation. If one of them needs to move, it moves alone.
// const DIMMED_ALPHA = 0.6; // exported for the tension symbols (Games/Shared/tensionSymbols)
// export const DIMMED_ALPHA = 0.6;
// 0.7 (Vladimir, 2026-10-01: "the symbols are quite bright and flashy and
// just 0.6 is not cutting it") — the win dim and the anticipation dim both.
export const DIMMED_ALPHA = 0.75;

// Fade animation duration (ms)
//
// ── FAST is the win presentation's fade, both ways ──
//
// dimReels(true) raises the dim as a win is shown, and undimReels(true) takes
// it back down; every caller inside the win cycle passes the fast flag. 50ms is
// about three frames — long enough to read as a fade rather than a cut, short
// enough that it never delays the line it belongs to.
//
// It is NOT the whole story: `snapClear()` still takes the dim off in a single
// synchronous frame, and that one must stay instant. It is called from
// handleSpin's cleanup, and the comment there records why — a 100ms undim fade
// once let the reels start spinning while the dim was still on screen.
const DIM_FADE_DURATION = 250;
const DIM_FADE_DURATION_FAST = 50;
// The per-reel TENSION dim's fade — between the two above: 250 read as slow,
// 50 as a cut (Vladimir, 2026-09-29: "something in between").
// const DIM_FADE_DURATION_TENSION = 120;
// const DIM_FADE_DURATION_TENSION = 250; // exported for the tension symbols (Games/Shared/tensionSymbols)
export const DIM_FADE_DURATION_TENSION = 250; // back to the board dim's speed, without the start wait
// Its fade IN only — shorter (Vladimir, 2026-09-29: "shorten the fadein to
// 150"). The fade out stays DIM_FADE_DURATION_TENSION.
// const DIM_FADE_IN_TENSION = 150; // exported for the tension symbols (Games/Shared/tensionSymbols)
export const DIM_FADE_IN_TENSION = 150;

// Buffer padding as a fraction of symbol height — scales uniformly across resolutions.
// At SYMBOL_HEIGHT=252, 0.016 ≈ 4px per side.
const DIM_PADDING_MULTIPLIER = 0.016;

// Unified dimming overlay for both main game and free spins
const DimReelSet = memo(
  // `paddingMultiplier` — how far the dim reaches past the grid, as a fraction
  // of symbol height. Defaults to DIM_PADDING_MULTIPLIER. A scene whose reel
  // slot is CLIPPED to its window (game_view's `reels_window_clip`) passes a
  // larger value, so the dim overlaps the window on every side and the clip
  // trims it to exactly the window. An unclipped scene keeps the default, or
  // the dim would spill over its frame.
  forwardRef(({ symbolWidth, symbolHeight, dividerDimensions, paddingMultiplier = DIM_PADDING_MULTIPLIER }, ref) => {
    const dimSpriteRef = useRef(null);
    const currentAlphaRef = useRef(0); // Track current alpha to persist across re-renders
    const fadeAnimRef = useRef(null); // Track active anime.js fade tween

    // ── Per-reel dim (the anticipation's "reels waiting their turn") ──
    //
    // One column of the same black sheet per reel, so the reel on its turn
    // stays lit. Same DIMMED_ALPHA and the same fades as the board dim
    // (Vladimir, 2026-09-29: "match them"). Together the columns tile the
    // board sheet exactly — the seams are halfway between reels.
    const reelSpriteRefs = useRef([]);
    const reelAlphaRefs = useRef([]);
    const reelFadeRefs = useRef([]);

    const fadeReel = (index, alpha, duration, easing) => {
      const sprite = reelSpriteRefs.current[index];
      if (!sprite) return;
      reelFadeRefs.current[index]?.pause();
      reelFadeRefs.current[index] = null;
      reelAlphaRefs.current[index] = alpha;
      if (duration === 0) {
        sprite.alpha = alpha;
        return;
      }
      reelFadeRefs.current[index] = anime({
        targets: sprite,
        alpha,
        duration,
        easing,
        complete: () => {
          reelFadeRefs.current[index] = null;
        },
      });
    };

    // Memoize sprite dimensions with padding buffer
    const spriteDimensions = useMemo(
      () => {
        const padding = Math.round(symbolHeight * paddingMultiplier);
        return {
          width:
            symbolWidth * Settings.REEL_COUNT +
            dividerDimensions.width * (Settings.REEL_COUNT - 1) +
            padding * 2,
          height: symbolHeight * Settings.SYMBOLS_VISIBLE * 1.0 + padding * 2,
        };
      },
      [symbolWidth, symbolHeight, dividerDimensions.width, paddingMultiplier],
    );

    // Column i spans from the seam before reel i to the seam after it; the
    // outer columns run to the board sheet's edges (padding included).
    const reelColumns = useMemo(() => {
      const count = Settings.REEL_COUNT;
      const spacing = symbolWidth + dividerDimensions.width;
      const halfWidth = spriteDimensions.width / 2;
      const edge = (k) =>
        k === 0 ? -halfWidth : k === count ? halfWidth : spacing * (k - count / 2);
      return Array.from({ length: count }, (_, i) => ({
        x: edge(i),
        width: edge(i + 1) - edge(i),
      }));
    }, [symbolWidth, dividerDimensions.width, spriteDimensions.width]);

    // Expose dimming control methods through ref
    useImperativeHandle(
      ref,
      () => ({
        // Dim the reels — pass fast=true for quicker 100ms fade (win presentation)
        dimReels: (fast = false) => {
          if (!dimSpriteRef.current) return;

          // Cancel any in-progress fade
          if (fadeAnimRef.current) {
            fadeAnimRef.current.pause();
            fadeAnimRef.current = null;
          }

          currentAlphaRef.current = DIMMED_ALPHA;
          fadeAnimRef.current = anime({
            targets: dimSpriteRef.current,
            alpha: DIMMED_ALPHA,
            duration: fast ? DIM_FADE_DURATION_FAST : DIM_FADE_DURATION,
            easing: "easeOutQuad",
            complete: () => {
              fadeAnimRef.current = null;
            },
          });
        },

        // Remove dimming — pass instant=true for quick sequential fade (returns Promise)
        undimReels: (instant = false) => {
          if (!dimSpriteRef.current) return Promise.resolve();
          if (currentAlphaRef.current === 0 && !fadeAnimRef.current) return Promise.resolve();

          // Cancel any in-progress fade
          if (fadeAnimRef.current) {
            fadeAnimRef.current.pause();
            fadeAnimRef.current = null;
          }

          currentAlphaRef.current = 0;

          if (instant) {
            // Quick fade that resolves — caller awaits before clearing overlays/textures
            return new Promise((resolve) => {
              fadeAnimRef.current = anime({
                targets: dimSpriteRef.current,
                alpha: 0,
                duration: DIM_FADE_DURATION_FAST,
                easing: "easeOutQuad",
                complete: () => {
                  fadeAnimRef.current = null;
                  resolve();
                },
              });
            });
          } else {
            fadeAnimRef.current = anime({
              targets: dimSpriteRef.current,
              alpha: 0,
              duration: DIM_FADE_DURATION,
              easing: "easeInQuad",
              complete: () => {
                fadeAnimRef.current = null;
              },
            });
            return Promise.resolve();
          }
        },

        // Instant snap to alpha 0 — synchronous, no animation.
        // Used on spin start so reels never begin while dim is still visible.
        snapClear: () => {
          if (!dimSpriteRef.current) return;
          if (fadeAnimRef.current) {
            fadeAnimRef.current.pause();
            fadeAnimRef.current = null;
          }
          currentAlphaRef.current = 0;
          dimSpriteRef.current.alpha = 0;
        },

        // One reel: the board dim's fade in (DIM_FADE_DURATION, easeOutQuad).
        // `fast` = the win presentation's own fade (the tension dim uses it).
        dimReel: (index, fast = false) => {
          if (reelAlphaRefs.current[index] === DIMMED_ALPHA) return;
          fadeReel(
            index,
            DIMMED_ALPHA,
            // fast ? DIM_FADE_DURATION_FAST : DIM_FADE_DURATION,
            // fast ? DIM_FADE_DURATION_TENSION : DIM_FADE_DURATION,
            fast ? DIM_FADE_IN_TENSION : DIM_FADE_DURATION,
            // The tension dim fades LINEARLY — easeOutQuad's fast start read
            // as a snap mid-fade (Vladimir, 2026-09-29: "simple linear").
            fast ? "linear" : "easeOutQuad",
          );
        },

        // One reel: the board dim's fade out (DIM_FADE_DURATION, easeInQuad).
        // instant=true snaps it — a new round never starts under a dim.
        undimReel: (index, instant = false, fast = false) => {
          if (!reelAlphaRefs.current[index] && !reelFadeRefs.current[index]) return;
          const duration = instant
            ? 0
            : fast
              ? DIM_FADE_DURATION_TENSION // was DIM_FADE_DURATION_FAST
              : DIM_FADE_DURATION;
          // fadeReel(index, 0, duration, "easeInQuad");
          fadeReel(index, 0, duration, fast ? "linear" : "easeInQuad");
        },
      }),
      [],
    );

    // Cleanup anime.js tween on unmount to prevent targeting destroyed sprites
    useEffect(() => {
      return () => {
        if (fadeAnimRef.current) {
          fadeAnimRef.current.pause();
          fadeAnimRef.current = null;
        }
        reelFadeRefs.current.forEach((tween) => tween?.pause());
        reelFadeRefs.current = [];
      };
    }, []);

    // Preserve alpha state across re-renders (especially during window resize)
    useEffect(() => {
      if (dimSpriteRef.current && currentAlphaRef.current !== 0) {
        // Restore the alpha without animation when dimensions change
        dimSpriteRef.current.alpha = currentAlphaRef.current;
      }
      reelSpriteRefs.current.forEach((sprite, i) => {
        if (sprite && !reelFadeRefs.current[i] && reelAlphaRefs.current[i]) {
          sprite.alpha = reelAlphaRefs.current[i];
        }
      });
    }, [symbolWidth, symbolHeight, dividerDimensions]);

    return (
      <Container name="dim_overlay" zIndex={2}>
        <Sprite
          ref={dimSpriteRef}
          texture={PIXI.Texture.WHITE}
          width={spriteDimensions.width}
          height={spriteDimensions.height}
          anchor={0.5}
          tint={OVERLAY_BACKGROUND_COLOR}
          alpha={0}
          name="dim_overlay_placeholder"
        />
        {reelColumns.map((column, i) => (
          <Sprite
            key={i}
            ref={(sprite) => {
              reelSpriteRefs.current[i] = sprite;
            }}
            texture={PIXI.Texture.WHITE}
            x={column.x}
            width={column.width}
            height={spriteDimensions.height}
            anchor={[0, 0.5]}
            tint={OVERLAY_BACKGROUND_COLOR}
            alpha={0}
            name={`dim_reel_${i}`}
          />
        ))}
      </Container>
    );
  }),
);

DimReelSet.displayName = "DimReelSet";

export default DimReelSet;
